import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { MinusIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { classNames } from '../utils/classNames';
import formatNumber from '../utils/formatNumber';
import { Chart } from './Chartjs';
import { ClubStat } from './ClubList';
import Tooltip from './Tooltip';

function Double({
  items,
  tooltip,
  highlight = false,
  highlightColor = 'bg-primary-350 text-gray-500',
}: {
  items: {
    title: string;
    Icon?: React.ReactNode;
    sign: string;
    value: number;
    className?: string;
  }[];
  tooltip: string;
  highlight?: boolean;
  highlightColor?: string;
}) {
  return (
    <div
      className={classNames(
        'rounded-2xl flex col-span-2 justify-between items-start  gap-1 w-full px-3 sm:px-4 py-2 ',
        highlight ? highlightColor : 'bg-gray-100 text-gray-400',
      )}
    >
      <div className="grid grid-cols-3 w-full">
        {items?.map((item, i) => (
          <div
            key={i}
            className={classNames(
              'w- shrink-1  overflow-hidden',
              i == items.length - 1 ? (highlight ? 'text-primary-500' : 'text-gray-500') : null,
              item.className,
            )}
          >
            <div className="flex  justify-between items-center gap-2">
              <span className="text-xs text-ellipsis overflow-hidden">{item.title}</span>
            </div>

            <div className="text-xl flex items-center gap-3">
              {item.Icon && (
                //@ts-ignore
                <item.Icon className="w-6 h-6" />
              )}
              {item.sign}
              {formatNumber(item.value) || <Skeleton height={16} width={100} />}
            </div>
          </div>
        ))}
      </div>
      <div className="w-4 h-4">
        <Tooltip content={tooltip} headline={items[0].title} mode="modal">
          <InformationCircleIcon className="w-4 h-4" />
        </Tooltip>
      </div>
    </div>
  );
}

const Large = ({
  title,
  value,
  genderData,
  compareValue,
}: {
  title: string;
  value: number;
  genderData: { label: string; value: number }[];
  compareValue: number | string;
}) => {
  const genderSum = genderData.reduce((acc, curr) => acc + curr.value, 0);
  const data = genderData.filter((d) => d.value > 0);

  const TrendingIcon = useCallback((change) => {
    if (change === 0) return <MinusIcon className="text-white max-[330px]:hidden w-8 h-8 sm:w-9 sm:h-9" />;
    if (change > 0) return <ArrowTrendingUpIcon className="text-white max-[330px]:hidden  w-8 h-8 sm:w-9 sm:h-9" />;
    if (change < 0) return <ArrowTrendingDownIcon className="text-white max-[330px]:hidden w-8 h-8 sm:w-9 sm:h-9" />;
  }, []);

  return (
    <div className="col-span-3 bg-primary-500 rounded-xl px-3 sm:px-4 p-4">
      <div className="text-white text-sm">{title}</div>

      <div className="grid grid-cols-2 gap-2 items-center">
        <div className="flex gap-4 items-center">
          {TrendingIcon(Number(compareValue))}

          <ClubStat
            large
            loading={value === undefined}
            value={value}
            compareValue={compareValue}
            signValue={false}
            name="Aktive Mitglieder"
            textColor="text-white"
          />
        </div>
        <div className="w-full  grid-cols-2 grid gap-1 items-center" key={JSON.stringify(genderData)}>
          <Chart
            className="w-full p-1"
            type="pie"
            responsive={true}
            maintainAspectRatio={false}
            options={{
              plugins: {
                legend: {
                  display: false,
                  position: 'right',
                },
              },
            }}
            data={{
              labels: data.map((d) => d.label),
              datasets: [
                {
                  data: data.map((d) => d.value),
                  backgroundColor: ['rgba(156, 163, 175, 1)', 'rgba(248, 248, 248, 1)', 'rgba(209, 213, 219, 1)'],
                  borderWidth: 0,
                },
              ],
            }}
          />
          <div className="flex flex-col">
            {data.map((d, i) => (
              <span key={i} className="text-[10px] text-gray-300">
                {typeof d.value == 'number' && genderSum && ((d.value / genderSum) * 100).toFixed(0)}% {d.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function Plain({
  tooltip,
  children,
  backgroundColor = 'bg-gray-100',
}: {
  tooltip: React.ReactNode;
  children: React.ReactNode;
  backgroundColor?: string;
}) {
  return (
    <div
      className={classNames(
        'rounded-2xl flex justify-between items-start gap-1 w-full px-3 sm:px-4 py-3',
        backgroundColor,
      )}
    >
      {children}
      <div className="w-4 h-4">
        <Tooltip content={tooltip} mode="modal">
          <InformationCircleIcon className="w-4 h-4" />
        </Tooltip>
      </div>
    </div>
  );
}
export const StatsCard = {
  Double,
  Large,
  Plain,
};
